import cn from 'classnames';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import { useState } from 'react';
import { useMedia } from 'react-use';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const Slider = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const isDesktop = useMedia('(min-width: 1024px)');
  const [sliderRef, slider] = useKeenSlider({
    loop: false,
    drag: !isDesktop,
    slides: {
      origin: 'center',
      perView: 1,
      spacing: 16
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    created() {
      setLoaded(true);
    }
  });

  return (
    <>
      <div ref={sliderRef} className="relative keen-slider">
        {loaded && slider.current && (
          <>
            <button
              onClick={() => slider.current?.prev()}
              className="absolute z-10 hidden w-10 h-10 transition-colors duration-200 -translate-y-1/2 rounded-full bg-neutral-950 not:disabled:hover:bg-black lg:block disabled:cursor-not-allowed disabled:opacity-60 focus:outline-white not:disabled:focus:outline-1 focus:outline-offset-1 left-4 top-1/2"
              type="button"
              disabled={currentSlide === 0}
            >
              {'<-'}
            </button>
            <button
              onClick={() => slider.current?.next()}
              className="absolute z-10 hidden w-10 h-10 transition-colors duration-200 -translate-y-1/2 rounded-full bg-neutral-950 not:disabled:hover:bg-black lg:block disabled:cursor-not-allowed disabled:opacity-60 focus:outline-white not:disabled:focus:outline-1 focus:outline-offset-1 right-4 top-1/2"
              type="button"
              disabled={currentSlide === slides.length - 1}
            >
              {'->'}
            </button>
          </>
        )}
        {slides.map((slide, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="flex keen-slider__slide">
            <Image
              src={slide.src}
              alt={slide.alt}
              width={slide.width}
              height={slide.height}
              quality={100}
            />
          </div>
        ))}
      </div>
      {loaded && slider && (
        <div className="flex justify-center mx-auto mt-6 space-x-2 max-w-max lg:hidden">
          {[...Array(slider.current.slides.length).keys()].map(idx => (
            <span
              key={idx}
              onClick={() => slider.current?.moveToIdx(idx)}
              className={cn(
                'cursor-pointer rounded-full w-2 h-2',
                currentSlide === idx ? 'bg-white' : 'bg-plain-neutral-4'
              )}
            />
          ))}
        </div>
      )}
    </>
  );
};

const Feature = ({ content }) => (
  <li className="list-none">
    <Content content={content} />
  </li>
);

const Content = ({ content }) => {
  const [isOpened, setIsOpened] = useState(false);

  const checklist = content.checklist.slice(0, isOpened ? content.lenght : 1);
  const toggleIsOpened = () => setIsOpened(prevState => !prevState);

  return (
    <div>
      {content.img && (
        <Image
          className="rounded-2xl mb-6 lg:mb-9 w-full"
          src={content.img.url}
          alt={content.img.alt}
          quality={100}
          width={content.img.width || '40'}
          height={content.img.height || '40'}
        />
      )}
      {content.slider && (
        <div className="mb-6">
          <Slider slides={content.slider.slides} />
        </div>
      )}
      <p className="mb-4 lg:mb-4 text-2xl leading-10 text-white font-aeonik">{content.title}</p>
      <p className="mb-6 lg:mb-8 max-w-max bg-scale-mauve text-scale-lightmauve bg-opacity-10 rounded-3xl md:px-4 md:py-0 lg:text-lg lg:leading-8 py-1.5 px-2">
        <strong>ROI: </strong>
        {content.roi}
      </p>
      <ReactMarkdown
        className="text-neutral-400 prose-a:light:text-link prose-a:dark:text-white max-w-none"
        rehypePlugins={[rehypeRaw]}
      >
        {content.text}
      </ReactMarkdown>
      {content.checklist && (
        <ul className="space-y-4 text-lg leading-6 mb-7 lg:mb-9 overflow-hidden">
          <p className="mb-6 lg:mb-5">How</p>

          {checklist.map((each, index) => {
            // from Builder comes as each.item, but locally we use it as a string
            const item = typeof each === 'string' ? each : each.item;

            return (
              <li key={item}>
                <span className="flex space-x-4 items-center md:items-start">
                  <CheckCircle className="md:mt-1" />
                  <p className="flex-1 lg:text-lg lg:leading-6 text-white">{item}</p>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {content.checklist.length > 1 && (
        <button onClick={toggleIsOpened}>
          Learn More&nbsp;
          <span
            className={cn(
              'transition-transform duration-300 ease-out inline-block font-aeonik',
              isOpened && 'rotate-180'
            )}
          >
            ↓
          </span>
        </button>
      )}
    </div>
  );
};

const CheckCircle = ({ className = '' }) => (
  <svg className={className} width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#D1AAD7" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.586 4.358a.709.709 0 0 1 .894-.01.695.695 0 0 1 .075.998l-4.97 5.967a1.341 1.341 0 0 1-.291.273 1.411 1.411 0 0 1-1.958-.395L3.38 8.254a.71.71 0 0 1 .197-.979.713.713 0 0 1 .782 0l2.06 1.365 5.158-4.292.01.01Z"
      fill="#000"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm0 0c-4.414 0-8-3.586-8-8s3.586-8 8-8 8 3.586 8 8-3.586 8-8 8Z"
      fill="#E5E5E5"
    />
  </svg>
);

export default Feature;
